import React, { useState, useEffect } from "react"
import { FiX, FiAlertCircle, FiUpload } from "react-icons/fi"
import toast from "react-hot-toast"
import axiosInstance from "../../../libs/axios"
import { useAuth } from "../../../provider/auth.provider"
import { useDebounce } from "../../../hooks/useDebounce"
import { SupplierPaymentList, type SPFilters } from "./list"
import type { PaginationType } from "../../../utils/pagination.utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface RestrictionStatus {
  is_blocked: boolean
  block_reason: string | null
  limit: { max_amount: number; reason: string | null } | null
}

interface CreateFormData {
  supplier_name: string
  reason_for_request: string
  other_remarks: string
  request_description: string
  total_payable_amount: string
  bank_name: string
  ifsc_code: string
  account_number: string
  upi_id: string
}

const EMPTY_FORM: CreateFormData = {
  supplier_name: "",
  reason_for_request: "",
  other_remarks: "",
  request_description: "",
  total_payable_amount: "",
  bank_name: "",
  ifsc_code: "",
  account_number: "",
  upi_id: "",
}

const FileUploadInput: React.FC<{ files: File[]; onChange: (files: File[]) => void; label?: string }> = ({ files, onChange, label = "Attach Documents (optional)" }) => {
  const [inputKey, setInputKey] = React.useState(0)
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-gray-400 hover:text-blue-600">
        <FiUpload className="w-4 h-4 flex-shrink-0" />
        <span>Click to attach files</span>
        <input key={inputKey} type="file" multiple className="hidden" onChange={(e) => {
          const picked = Array.from(e.target.files || [])
          if (picked.length) { onChange([...files, ...picked]); setInputKey(k => k + 1) }
        }} />
      </label>
      {files.length > 0 && (
        <ul className="mt-1 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs border border-gray-100">
              <span className="truncate text-gray-700">{f.name}</span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0"><FiX className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const DEFAULT_PAGINATION: PaginationType = {
  offset: 0, limit: 10, page: 1,
  total_pages: 0, total_count: 0,
  has_next_page: false, has_prev_page: false,
  next_page: null, prev_page: null,
}

// ─── Main Component ───────────────────────────────────────────────────────────

const SupplierPaymentPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission("supplier_payment", "create")
  const canViewAll = hasPermission("supplier_payment", "view_all")
  const canViewOwn = hasPermission("supplier_payment", "view_own")
  const canApprove = hasPermission("supplier_payment", "approve")
  const canTransfer = hasPermission("supplier_payment", "transfer")
  const canEdit = hasPermission("supplier_payment", "edit_requested")

  const [requests, setRequests] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType>(DEFAULT_PAGINATION)
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)

  // Sort state
  const [sortBy, setSortBy] = useState("created_at")
  const [sortOrder, setSortOrder] = useState("DESC")

  // Advanced filters state
  const [filters, setFilters] = useState<SPFilters>({})

  const [restrictionStatus, setRestrictionStatus] = useState<RestrictionStatus>({
    is_blocked: false, block_reason: null, limit: null,
  })

  // Create / Edit form
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<CreateFormData>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<Partial<CreateFormData>>({})
  const [saving, setSaving] = useState(false)
  const [requestFiles, setRequestFiles] = useState<File[]>([])
  const [qrFile, setQrFile] = useState<File | null>(null)
  const [qrInputKey, setQrInputKey] = useState(0)

  // ─── Fetch restriction status ───────────────────────────────────────────────

  useEffect(() => {
    if (canCreate || canViewOwn) {
      axiosInstance.get("/supplier-payment/restrictions/my-status")
        .then((res) => { if (res.data.success) setRestrictionStatus(res.data.data) })
        .catch(() => {})
    }
  }, [canCreate, canViewOwn])

  // ─── Fetch list ─────────────────────────────────────────────────────────────

  const fetchRequests = async (
    page = pagination.page,
    status = statusFilter,
    search = debouncedSearch,
    limitVal = pagination.limit,
    sb = sortBy,
    so = sortOrder,
    f = filters,
  ) => {
    if (!canViewAll && !canViewOwn && !canApprove) return
    try {
      setLoading(true)
      const params: Record<string, any> = { page, limit: limitVal, sort_by: sb, sort_order: so }
      if (status) params.status = status
      if (search) params.search = search
      if (!canViewAll) params.view_own = true
      // Advanced filters
      if (f.employee_id) params.employee_id = f.employee_id
      if (f.reason_for_request) params.reason_for_request = f.reason_for_request
      if (f.amount_min) params.amount_min = f.amount_min
      if (f.amount_max) params.amount_max = f.amount_max
      if (f.created_at_from) params.created_at_from = f.created_at_from
      if (f.created_at_to) params.created_at_to = f.created_at_to
      if (f.approved_at_from) params.approved_at_from = f.approved_at_from
      if (f.approved_at_to) params.approved_at_to = f.approved_at_to
      if (f.transfer_date_from) params.transfer_date_from = f.transfer_date_from
      if (f.transfer_date_to) params.transfer_date_to = f.transfer_date_to
      if (f.approved_by) params.approved_by = f.approved_by

      const res = await axiosInstance.get("/supplier-payment/list", { params })
      if (res.data.success) {
        setRequests(res.data.data.requests || [])
        setPagination(res.data.data.pagination || DEFAULT_PAGINATION)
      }
    } catch {
      toast.error("Failed to load requests")
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field: string) => {
    const newOrder = sortBy === field && sortOrder === "ASC" ? "DESC" : "ASC"
    setSortBy(field)
    setSortOrder(newOrder)
    fetchRequests(1, statusFilter, debouncedSearch, pagination.limit, field, newOrder, filters)
  }

  const handleApplyFilters = () => {
    fetchRequests(1, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, filters)
  }

  const handleResetFilters = () => {
    const empty: SPFilters = {}
    setFilters(empty)
    fetchRequests(1, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, empty)
  }

  useEffect(() => {
    fetchRequests(1, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    fetchRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Create form handlers ────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const errs: Partial<CreateFormData> = {}
    if (!form.supplier_name.trim()) errs.supplier_name = "Supplier name is required"
    if (!form.reason_for_request) errs.reason_for_request = "Reason is required"
    if (!form.total_payable_amount || Number(form.total_payable_amount) <= 0)
      errs.total_payable_amount = "A valid amount is required"
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleEdit = (req: any) => {
    setEditingId(req.id)
    setForm({
      supplier_name: req.supplier_name || "",
      reason_for_request: req.reason_for_request || "",
      other_remarks: req.other_remarks || "",
      request_description: req.request_description || "",
      total_payable_amount: req.total_payable_amount != null ? String(req.total_payable_amount) : "",
      bank_name: req.bank_name || "",
      ifsc_code: req.ifsc_code || "",
      account_number: req.account_number || "",
      upi_id: req.upi_id || "",
    })
    setFormErrors({})
    setRequestFiles([])
    setQrFile(null)
    setShowCreate(true)
  }

  const handleUpdate = async () => {
    if (!validateForm() || editingId == null) return
    setSaving(true)
    try {
      const payload = {
        supplier_name: form.supplier_name.trim(),
        reason_for_request: form.reason_for_request,
        other_remarks: form.other_remarks.trim() || null,
        request_description: form.request_description.trim() || null,
        total_payable_amount: Number(form.total_payable_amount),
        bank_name: form.bank_name.trim() || null,
        ifsc_code: form.ifsc_code.trim() || null,
        account_number: form.account_number.trim() || null,
        upi_id: form.upi_id.trim() || null,
      }
      const res = await axiosInstance.put(`/supplier-payment/${editingId}/update`, payload)
      if (res.data.success) {
        toast.success("Supplier payment request updated")
        handleCloseCreate()
        fetchRequests(pagination.page, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, filters)
      } else {
        toast.error(res.data.message || "Failed to update request")
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update request")
    } finally {
      setSaving(false)
    }
  }

  const handleCreate = async () => {
    if (!validateForm()) return
    setSaving(true)
    try {
      const fd = new FormData()
      fd.append("supplier_name", form.supplier_name.trim())
      fd.append("reason_for_request", form.reason_for_request)
      fd.append("total_payable_amount", String(Number(form.total_payable_amount)))
      if (form.other_remarks.trim()) fd.append("other_remarks", form.other_remarks.trim())
      if (form.request_description.trim()) fd.append("request_description", form.request_description.trim())
      if (form.bank_name.trim()) fd.append("bank_name", form.bank_name.trim())
      if (form.ifsc_code.trim()) fd.append("ifsc_code", form.ifsc_code.trim())
      if (form.account_number.trim()) fd.append("account_number", form.account_number.trim())
      if (form.upi_id.trim()) fd.append("upi_id", form.upi_id.trim())
      if (qrFile) fd.append("qr_image", qrFile)
      requestFiles.forEach(f => fd.append("documents", f))

      const res = await axiosInstance.post("/supplier-payment/create", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      if (res.data.success) {
        toast.success("Supplier payment request created")
        setShowCreate(false)
        setForm(EMPTY_FORM)
        setFormErrors({})
        setRequestFiles([])
        setQrFile(null)
        fetchRequests(1, statusFilter, debouncedSearch, pagination.limit)
      } else {
        toast.error(res.data.message || "Failed to create request")
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create request")
    } finally {
      setSaving(false)
    }
  }

  const handleCloseCreate = () => {
    if (saving) return
    setShowCreate(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormErrors({})
    setRequestFiles([])
    setQrFile(null)
  }

  // ─── Input helper ────────────────────────────────────────────────────────────

  const setField = (key: keyof CreateFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }))
    if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Blocked warning */}
      {restrictionStatus.is_blocked && (
        <div className="mb-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded">
          <FiAlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">You are blocked from creating supplier payment requests</p>
            {restrictionStatus.block_reason && (
              <p className="text-xs text-red-600 mt-0.5">Reason: {restrictionStatus.block_reason}</p>
            )}
          </div>
        </div>
      )}

      {/* Limit warning */}
      {restrictionStatus.limit && (
        <div className="mb-4 flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <FiAlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Your role has a maximum request limit of ₹{Number(restrictionStatus.limit.max_amount).toLocaleString("en-IN")} per request
            {restrictionStatus.limit.reason ? ` (${restrictionStatus.limit.reason})` : ""}
          </p>
        </div>
      )}

      <SupplierPaymentList
        requests={requests}
        pagination={pagination}
        loading={loading}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onSearch={(v) => { setSearchTerm(v) }}
        onStatusChange={(v) => { setStatusFilter(v); fetchRequests(1, v, debouncedSearch, pagination.limit, sortBy, sortOrder, filters) }}
        onPageChange={(p) => fetchRequests(p, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, filters)}
        onLimitChange={(l) => { setPagination((prev) => ({ ...prev, limit: l, page: 1 })); fetchRequests(1, statusFilter, debouncedSearch, l, sortBy, sortOrder, filters) }}
        onCreateRequest={() => setShowCreate(true)}
        canCreate={canCreate && !restrictionStatus.is_blocked}
        canViewAll={canViewAll}
        canTransfer={canTransfer}
        canEdit={canEdit}
        onEditRequest={handleEdit}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={filters}
        onFiltersChange={setFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        onRefresh={() => fetchRequests(1, statusFilter, debouncedSearch, pagination.limit, sortBy, sortOrder, filters)}
      />

      {/* Create Offcanvas */}
      {showCreate && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCloseCreate} />
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between p-5 border-b bg-gray-50">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{editingId ? "Edit Supplier Payment Request" : "New Supplier Payment Request"}</h3>
                <p className="text-sm text-gray-500 mt-0.5">Fill in the details below</p>
              </div>
              <button onClick={handleCloseCreate} disabled={saving}
                className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50">
                <FiX size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Supplier Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name *</label>
                <input type="text" value={form.supplier_name} onChange={setField("supplier_name")}
                  placeholder="Enter supplier name"
                  className={`w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 ${formErrors.supplier_name ? "border-red-400 bg-red-50" : "border-gray-300"}`} />
                {formErrors.supplier_name && <p className="text-xs text-red-600 mt-1">{formErrors.supplier_name}</p>}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Request *</label>
                <select value={form.reason_for_request} onChange={setField("reason_for_request")}
                  className={`w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 ${formErrors.reason_for_request ? "border-red-400 bg-red-50" : "border-gray-300"}`}>
                  <option value="">Select reason</option>
                  <option value="purchase">Purchase</option>
                  <option value="repair">Repair</option>
                  <option value="office_expenses">Office Expenses</option>
                  <option value="other">Other</option>
                </select>
                {formErrors.reason_for_request && <p className="text-xs text-red-600 mt-1">{formErrors.reason_for_request}</p>}
              </div>

              {/* Other Remarks (shown when reason is "other") */}
              {form.reason_for_request === "other" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Remarks</label>
                  <input type="text" value={form.other_remarks} onChange={setField("other_remarks")}
                    placeholder="Specify the reason"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
              )}

              {/* Total Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Payable Amount (₹) *</label>
                <input type="number" value={form.total_payable_amount} onChange={setField("total_payable_amount")}
                  placeholder="Enter amount" min={1}
                  className={`w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 ${formErrors.total_payable_amount ? "border-red-400 bg-red-50" : "border-gray-300"}`} />
                {formErrors.total_payable_amount && <p className="text-xs text-red-600 mt-1">{formErrors.total_payable_amount}</p>}
                {restrictionStatus.limit && (
                  <p className="text-xs text-yellow-600 mt-1">Role limit: ₹{Number(restrictionStatus.limit.max_amount).toLocaleString("en-IN")}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Description</label>
                <textarea value={form.request_description} onChange={setField("request_description")}
                  rows={3} placeholder="Describe the purpose of this payment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Payment Details */}
              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Details (optional)</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Bank Name</label>
                    <input type="text" value={form.bank_name} onChange={setField("bank_name")}
                      placeholder="e.g. HDFC Bank"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Account Number</label>
                      <input type="text" value={form.account_number} onChange={setField("account_number")}
                        placeholder="Account number"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 font-mono" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">IFSC Code</label>
                      <input type="text" value={form.ifsc_code} onChange={setField("ifsc_code")}
                        placeholder="e.g. HDFC0001234"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">UPI ID</label>
                    <input type="text" value={form.upi_id} onChange={setField("upi_id")}
                      placeholder="e.g. supplier@upi"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  {!editingId && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">QR Code (optional)</label>
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-gray-400 hover:text-blue-600">
                        <FiUpload className="w-4 h-4 flex-shrink-0" />
                        <span>{qrFile ? qrFile.name : "Upload QR code image"}</span>
                        <input key={qrInputKey} type="file" accept="image/*" className="hidden" onChange={(e) => {
                          const f = e.target.files?.[0] || null
                          setQrFile(f)
                          setQrInputKey(k => k + 1)
                        }} />
                      </label>
                      {qrFile && (
                        <button type="button" onClick={() => setQrFile(null)} className="text-xs text-red-500 hover:underline mt-1">Remove</button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!editingId && (
                <div className="border-t border-gray-100 pt-4">
                  <FileUploadInput files={requestFiles} onChange={setRequestFiles} />
                </div>
              )}
            </div>

            <div className="border-t bg-gray-50 px-5 py-4 flex justify-end gap-3">
              <button onClick={handleCloseCreate} disabled={saving}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={editingId ? handleUpdate : handleCreate} disabled={saving}
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded hover:bg-blue-800 disabled:opacity-50">
                {saving ? (editingId ? "Updating..." : "Submitting...") : (editingId ? "Update Request" : "Submit Request")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default SupplierPaymentPage
